import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import {
  CONTACT_INQUIRY_CATEGORIES,
  CONTACT_INQUIRY_STATUSES,
  type ContactInquiryCategory,
  type ContactInquiryStatus,
} from '@kulum/shared';
import { db } from './firebase';

const categorySet: ReadonlySet<string> = new Set(CONTACT_INQUIRY_CATEGORIES);
const statusSet: ReadonlySet<string> = new Set(CONTACT_INQUIRY_STATUSES);

const DEFAULT_CATEGORY: ContactInquiryCategory = 'other';
const DEFAULT_STATUS: ContactInquiryStatus = 'open';

export interface AdminContactInquiry {
  id: string;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  category: ContactInquiryCategory;
  message: string;
  status: ContactInquiryStatus;
  adminNote: string;
  createdAt?: Date;
  updatedAt?: Date;
  resolvedAt?: Date | null;
  lastUpdatedBy?: string;
}

interface ContactInquiryDoc {
  userId?: unknown;
  userEmail?: unknown;
  userDisplayName?: unknown;
  category?: unknown;
  message?: unknown;
  status?: unknown;
  adminNote?: unknown;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  resolvedAt?: Timestamp | null;
  lastUpdatedBy?: unknown;
}

function parseCategory(value: unknown): ContactInquiryCategory {
  if (typeof value === 'string' && categorySet.has(value)) {
    return value as ContactInquiryCategory;
  }
  return DEFAULT_CATEGORY;
}

function parseStatus(value: unknown): ContactInquiryStatus {
  if (typeof value === 'string' && statusSet.has(value)) {
    return value as ContactInquiryStatus;
  }
  return DEFAULT_STATUS;
}

function parseDate(value: unknown): Date | undefined {
  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as Timestamp).toDate();
  }
  return undefined;
}

function mapContactInquiry(id: string, data: ContactInquiryDoc): AdminContactInquiry {
  const resolvedAtTimestamp = data.resolvedAt;
  return {
    id,
    userId: typeof data.userId === 'string' ? data.userId : '',
    userEmail: typeof data.userEmail === 'string' ? data.userEmail : '',
    userDisplayName: typeof data.userDisplayName === 'string' ? data.userDisplayName : '不明',
    category: parseCategory(data.category),
    message: typeof data.message === 'string' ? data.message : '',
    status: parseStatus(data.status),
    adminNote: typeof data.adminNote === 'string' ? data.adminNote : '',
    createdAt: parseDate(data.createdAt),
    updatedAt: parseDate(data.updatedAt),
    resolvedAt: resolvedAtTimestamp ? parseDate(resolvedAtTimestamp) : null,
    lastUpdatedBy: typeof data.lastUpdatedBy === 'string' ? data.lastUpdatedBy : undefined,
  };
}

export async function fetchContactInquiries(maxCount: number = 200): Promise<AdminContactInquiry[]> {
  const inquiriesQuery = query(
    collection(db, 'contactInquiries'),
    orderBy('createdAt', 'desc'),
    limit(maxCount)
  );
  const snapshot = await getDocs(inquiriesQuery);

  return snapshot.docs.map((inquiryDoc) =>
    mapContactInquiry(inquiryDoc.id, inquiryDoc.data() as ContactInquiryDoc)
  );
}

export async function fetchActiveContactInquiryCount(): Promise<number> {
  const activeInquiriesQuery = query(
    collection(db, 'contactInquiries'),
    where('status', 'in', ['open', 'in_progress'])
  );
  const snapshot = await getCountFromServer(activeInquiriesQuery);
  return snapshot.data().count;
}

export async function fetchContactInquiryDetail(
  inquiryId: string
): Promise<AdminContactInquiry | null> {
  const inquiryRef = doc(db, 'contactInquiries', inquiryId);
  const inquirySnap = await getDoc(inquiryRef);

  if (!inquirySnap.exists()) {
    return null;
  }

  return mapContactInquiry(inquirySnap.id, inquirySnap.data() as ContactInquiryDoc);
}

interface UpdateContactInquiryInput {
  inquiryId: string;
  status: ContactInquiryStatus;
  adminNote: string;
  adminId: string;
}

export async function updateContactInquiry(input: UpdateContactInquiryInput): Promise<void> {
  const inquiryRef = doc(db, 'contactInquiries', input.inquiryId);
  const normalizedAdminNote = input.adminNote.trim();
  const shouldResolve = input.status === 'resolved';

  await updateDoc(inquiryRef, {
    status: input.status,
    adminNote: normalizedAdminNote,
    updatedAt: serverTimestamp(),
    resolvedAt: shouldResolve ? serverTimestamp() : null,
    lastUpdatedBy: input.adminId,
  });
}
